import { MetaStore } from "@pdfking/compatibility"
import { Init } from "@pdfking/reactivity/preact"
import { CreateSingleton, RecordAssign } from "@pdfking/tfunc"

import { NativeChrome } from "#compatibility"

import { DetectLang } from "../i18n/Languages"
import { set_language } from "../i18n/useTranslation"

// Everything that touches the browser is made on first use, never at import:
// a prerender (node) renders the switches too, on the plain `data` defaults,
// and the browser starts the real thing with StartSettings.

// A live MediaQueryList: `.matches` is the OS color scheme right now, so
// every save compares against a fresh value. It can flip mid-session, and a
// pick that now matches it has to drop the stored value rather than pin the
// app against it — same for `DetectLang()` on the language side.
const media = CreateSingleton(() => matchMedia("(prefers-color-scheme: dark)"))

// The host whose window material has to be told what the page is wearing, on
// the one platform that has one. Undefined in a browser tab and on the other
// desktop builds, and then none of this does anything.
const chrome = CreateSingleton(NativeChrome)

// Read once, before the first `set_dark` can make it lie.
//
// Telling the host moves the *app's* AppKit appearance, and that is also what
// `prefers-color-scheme` reports — so a live read would come back as whatever
// this app last chose, not what the OS prefers. Comparing a pick against that
// would call every explicit choice "same as the default" and throw it away.
// A browser tab has no such host and keeps the live reading it always had.
const os_dark = CreateSingleton(() => media().matches)
const default_dark = () => (chrome() ? os_dark() : media().matches)

type Persisted = { dark?: boolean; lang?: string }

const cache = CreateSingleton(() => MetaStore<boolean | string>("PdfViewSettings"))

/**
 * A host's chrome state: language and color scheme, behind the switches. The
 * viewer holds neither — they're not part of a document, so they don't belong
 * in the route (it reads the language back through the `lang` getter it's
 * built with) — and the host persists them here instead. A field equal to its default is deleted
 * rather than stored, so a visitor who never diverged (or switched back)
 * keeps following the browser/OS instead of being pinned to whatever it
 * happened to be on the day they clicked.
 */
export const Settings = Init({
  data: {
    dark: false,
    lang: "en" as string,
  },
  methods: {
    // The two side effects the viewer used to run inside its own render.
    apply() {
      set_language(this.lang)
      document.documentElement.classList.toggle("dark", this.dark)
      document.documentElement.classList.toggle("light", !this.dark)
      // The third side effect, and the only one that leaves the page: the
      // window's glass is drawn by AppKit, which cannot see a class on
      // `<html>` and would otherwise stay light behind a dark UI.
      chrome()?.set_dark(this.dark)
    },
    persist(key: keyof Persisted, value: boolean | string, fallback: boolean | string) {
      void (value === fallback ? cache().delete(key) : cache().set(key, value))
    },
  },
  watch: {
    dark(dark: boolean) {
      this.apply()
      this.persist("dark", dark, default_dark())
    },
    lang(lang: string) {
      this.apply()
      this.persist("lang", lang, DetectLang())
    },
  },
})

/**
 * Starts Settings in the browser, once: the browser/OS defaults, applied, then
 * the stored picks. The stored ones are asked for first — IndexedDB answers in
 * order, so the defaults' own write (dropping a key equal to its default)
 * can't land before that read.
 */
export const StartSettings = CreateSingleton(() => {
  os_dark()
  const saved = cache().list()
  RecordAssign(Settings, { dark: default_dark(), lang: DetectLang() as string })
  Settings.apply()
  // IndexedDB is async: the page starts on the defaults and snaps to the
  // stored picks once they land — a repaint at worst, every reader is reactive.
  void saved.then(stored => RecordAssign(Settings, stored as Persisted))
})
